import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, bootstrapFromStorage, clearTokens, setOnAuthChange, setTokens } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshMe = useCallback(async () => {
    try {
      const me = await api("/auth/me");
      setUser(me);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    bootstrapFromStorage();
    setOnAuthChange(() => {
      // re-fetch user when tokens change
      refreshMe().finally(() => setLoading(false));
    });
    refreshMe().finally(() => setLoading(false));
  }, [refreshMe]);

  const signin = useCallback(async (email, password, mfa_code) => {
    const data = await api("/auth/login", { method: "POST", body: { email, password, mfa_code } });
    setTokens(data);
    setUser(data.user);
    return data.user;
  }, []);

  const signup = useCallback(async (email, password, role_intent, display_name, details = {}) => {
    const data = await api("/auth/signup", {
      method: "POST",
      body: { email, password, role_intent, display_name, ...details },
    });
    setTokens(data);
    setUser(data.user);
    return data.user;
  }, []);

  const signout = useCallback(async () => {
    try { const {stopAvailabilityPush} = await import("@/components/DailyAvailability"); await stopAvailabilityPush(); } catch {}
    try { await api("/auth/logout", { method: "POST" }); } catch {}
    clearTokens();
    setUser(null);
  }, []);

  const signoutAll = useCallback(async () => {
    try { const {stopAvailabilityPush} = await import("@/components/DailyAvailability"); await stopAvailabilityPush(); } catch {}
    try { await api("/auth/logout-all", { method: "POST" }); } catch {}
    clearTokens();
    setUser(null);
  }, []);

  const hasRole = useCallback(
    (role) => Array.isArray(user?.roles) && user.roles.includes(role),
    [user]
  );

  return (
    <AuthCtx.Provider value={{ user, loading, signin, signup, signout, signoutAll, refreshMe, hasRole }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
